import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Form, FormService, Question } from "../services/formService";

type GetFormData = {
  data?: Form;
  hasError: boolean;
  isLoading: boolean;
  retry: () => void;
  updateAnswer: (text: string, index: number) => void;
  validate: () => boolean;
};

const TIMEOUT_INTERVAL_MS = 3000;

const useGetFormData = (): GetFormData => {
  const formService = new FormService();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<Form | undefined>();
  const [hasError, setHasError] = useState(false);
  const router = useRouter();
  const { id: formId } = router.query as any;

  const fetchForm = async () => {
    setHasError(false);
    setIsLoading(true);
    const formDataReponse = await formService.fetchForm({ id: formId });
    setIsLoading(false);
    if (!formDataReponse.success) {
      setHasError(true);
    }
    setData(formDataReponse.data?.form);
  };

  useEffect(() => {
    setIsLoading(true);
    let timer: NodeJS.Timeout;
    if (!formId) {
      timer = setTimeout(() => {
        // Needed as if the form id url param is not given then the form will load forever
        setIsLoading(false);
        setHasError(true);
      }, TIMEOUT_INTERVAL_MS);
      return;
    }
    () => clearTimeout(timer);
    fetchForm();
    return () => clearTimeout(timer);
  }, [formId]);

  const updateAnswer = (text: string, index: number) => {
    const questionsCopy = [...data!.questions];
    questionsCopy[index].answer = text;
    questionsCopy[index].hasError = false;
    setData({ ...data!, questions: questionsCopy });
  };

  const validate = () => {
    let isValid = true;
    const questionCopy = data?.questions.map((question) => {
      if (question.answer || !question.isRequired) {
        return { ...question, hasError: false };
      }
      isValid = false;
      return { ...question, hasError: true };
    }) as Question[];

    setData({ ...data!, questions: questionCopy });
    return isValid;
  };

  return {
    data,
    hasError,
    isLoading,
    retry: fetchForm,
    updateAnswer,
    validate,
  };
};

export { useGetFormData };
